import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, X, ImageIcon, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "../lib/api";

const MAX_DIMENSION = 1400; // px — resize the longer edge to this
const JPEG_QUALITY = 0.86;
const MAX_INPUT_MB = 12; // reject anything larger before client-side resize

/**
 * Resize an image File to a max dimension and return a JPEG/PNG Blob.
 * GIFs and animated formats are uploaded as-is (no canvas conversion).
 */
async function compressImage(file) {
  if (file.type === "image/gif") return file;

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });

  const longest = Math.max(img.width, img.height);
  if (longest <= MAX_DIMENSION) {
    return file; // already small enough
  }
  const scale = MAX_DIMENSION / longest;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);

  const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), outType, JPEG_QUALITY);
  });
}

/**
 * ImageUploader — drag/drop or click to pick a local file, OR paste an external URL.
 * Calls `onChange(url)` with a relative uploaded URL or the pasted external URL.
 */
export default function ImageUploader({ value, onChange, authedRequest, testIdPrefix = "image" }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("file"); // "file" | "url"
  const [urlInput, setUrlInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Resolve relative URL like "/api/images/abc" → absolute for preview.
  const resolved = resolveImageUrl(value);

  useEffect(() => {
    // If a URL was already provided externally, default the URL field to it.
    if (value && (value.startsWith("http") || value.startsWith("data:"))) {
      setUrlInput(value);
    }
  }, [value]);

  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > MAX_INPUT_MB * 1024 * 1024) {
      toast.error(`Image too large`, { description: `Max ${MAX_INPUT_MB}MB. Try a smaller file.` });
      return;
    }
    setBusy(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      const filename = file.name?.replace(/\.[^.]+$/, "") || "upload";
      const ext = compressed.type === "image/png" ? ".png" : compressed.type === "image/gif" ? ".gif" : ".jpg";
      fd.append("file", compressed, `${filename}${ext}`);

      const { data } = await authedRequest.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns relative URL — store as relative; preview resolves absolute.
      onChange(data.url);
      toast.success("Image uploaded", { description: `${(data.size / 1024).toFixed(0)} KB` });
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed. Try again.";
      toast.error("Upload failed", { description: msg });
    } finally {
      setBusy(false);
    }
  }, [authedRequest, onChange]);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    uploadFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    uploadFile(file);
  };

  const applyUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    onChange(trimmed);
    toast.success("Image URL set");
  };

  const clear = () => {
    onChange("");
    setUrlInput("");
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      {/* Mode toggle */}
      <div className="inline-flex p-1 bg-shay-cream rounded-full mb-3 border border-shay-gold/20">
        <button
          type="button"
          onClick={() => setMode("file")}
          data-testid={`${testIdPrefix}-mode-file`}
          className={`px-4 py-1.5 text-[10px] uppercase tracking-widest rounded-full transition-colors ${
            mode === "file" ? "bg-shay-navy text-shay-gold" : "text-shay-navy/70 hover:text-shay-navy"
          }`}
        >
          <Upload size={11} className="inline mr-1.5" />
          Upload from device
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          data-testid={`${testIdPrefix}-mode-url`}
          className={`px-4 py-1.5 text-[10px] uppercase tracking-widest rounded-full transition-colors ${
            mode === "url" ? "bg-shay-navy text-shay-gold" : "text-shay-navy/70 hover:text-shay-navy"
          }`}
        >
          <LinkIcon size={11} className="inline mr-1.5" />
          Use URL
        </button>
      </div>

      {/* Preview */}
      {resolved && (
        <div className="mb-3 relative inline-block" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={resolved}
            alt="Selected"
            className="h-32 w-32 object-cover rounded-sm border border-shay-gold/30"
          />
          <button
            type="button"
            onClick={clear}
            data-testid={`${testIdPrefix}-clear`}
            className="absolute -top-2 -right-2 h-7 w-7 grid place-items-center rounded-full bg-shay-navy text-shay-cream hover:bg-shay-gold hover:text-shay-navy transition-colors"
            aria-label="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {mode === "file" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          data-testid={`${testIdPrefix}-dropzone`}
          className={`border-2 border-dashed rounded-sm px-6 py-8 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-shay-gold bg-shay-gold/5"
              : "border-shay-gold/30 hover:border-shay-gold/60 hover:bg-shay-cream"
          } ${busy ? "opacity-60 cursor-wait" : ""}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onPick}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
            disabled={busy}
          />
          <div className="flex flex-col items-center gap-2">
            {busy ? (
              <Loader2 size={28} className="text-shay-gold animate-spin" />
            ) : (
              <ImageIcon size={28} className="text-shay-gold" strokeWidth={1.4} />
            )}
            <p className="text-sm text-shay-navy font-medium">
              {busy ? "Uploading..." : resolved ? "Replace image" : "Click to upload, or drag & drop"}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-shay-chocolate/60">
              JPG · PNG · WEBP · GIF · Up to {MAX_INPUT_MB} MB
            </p>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://..."
            data-testid={`${testIdPrefix}-url-input`}
            className="field-input flex-1"
          />
          <button
            type="button"
            onClick={applyUrl}
            data-testid={`${testIdPrefix}-url-apply`}
            className="px-5 py-2 bg-shay-navy text-shay-gold text-xs uppercase tracking-widest rounded-full hover:bg-shay-gold hover:text-shay-navy transition-colors"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
